import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { X, Sparkles, Loader2, ArrowRight, ArrowUpRight, CheckCircle2, Pencil, Save, Ban, Lock, Settings } from "lucide-react";
import { StringHelper } from "@bryntum/schedulerpro";

import { Button } from "@/modern/components/ui/button-shadcn";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/modern/components/ui/alert-dialog";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";

import { useFileDetailModal } from "../../context/FileDetailModalContext";
import { PromotePanelProvider, usePromotePanel, PROMOTE_STEPS } from "./context/PromotePanelContext";
import { StepIndicator } from "./components/StepIndicator";
import { ClassifyStep } from "./components/ClassifyStep";
import { DescribeStep } from "./components/DescribeStep";
import { OrganizeStep } from "./components/OrganizeStep";
import { SummaryStep } from "./components/SummaryStep";

const STEP_COMPONENTS = [ClassifyStep, DescribeStep, OrganizeStep, SummaryStep];

const LockedState = ({ onNavigateToSettings }: { onNavigateToSettings: () => void }) => (
  <div className="flex-1 flex items-center justify-center p-8">
    <div className="max-w-md w-full text-center space-y-6">
      <div className="flex justify-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-foreground">AI Key Required</h3>
        <p className="text-sm text-muted-foreground">
          An OpenAI API key needs to be added to use AI insights. Configure your API key in Settings to enable
          AI-powered analysis.
        </p>
      </div>
      <Button type="button" onClick={onNavigateToSettings} variant="outline" className="w-full">
        <Settings className="w-4 h-4 mr-2" />
        Go to Settings
      </Button>
    </div>
  </div>
);

const PromoteSuccessView = ({
  onDone,
  isEditing,
  onViewAssets,
}: {
  onDone: () => void;
  isEditing: boolean;
  onViewAssets: () => void;
}) => (
  <motion.div
    role="dialog"
    aria-modal="true"
    aria-labelledby="promote-success-title"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    className="flex min-h-0 flex-1 flex-col items-center justify-center bg-card px-5 py-8"
  >
    <div className="flex w-full max-w-sm flex-col items-center gap-5 text-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 22, delay: 0.05 }}
        className="relative"
      >
        <div className="absolute inset-0 scale-150 rounded-full bg-emerald-500/25 blur-xl" aria-hidden />
        <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15 ring-2 ring-emerald-500/40 dark:bg-emerald-400/10 dark:ring-emerald-400/35">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" strokeWidth={1.75} />
        </div>
      </motion.div>

      <div className="space-y-1.5">
        <motion.p
          id="promote-success-title"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.3 }}
          className="text-base font-semibold text-emerald-700 dark:text-emerald-400"
        >
          {isEditing ? "Changes saved successfully!" : "File promoted successfully!"}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          className="text-sm leading-relaxed text-muted-foreground"
        >
          {isEditing
            ? "Your promotion details have been updated."
            : "This file is promoted and ready for marketing."}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.26, duration: 0.35 }}
        className="flex w-full flex-col gap-4"
      >
        <Button type="button" onClick={onDone} className="w-full gap-2">
          Done
        </Button>
        <button
          type="button"
          onClick={onViewAssets}
          className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
        >
          View Promoted Assets
          <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </motion.div>
    </div>
  </motion.div>
);

const PromotePanelInner = () => {
  const { handleClosePromotePanel, entityRecord, setOpen, isPreparingForPromote } = useFileDetailModal();
  const { userPreferences, selectedBoard, viewManager, entityManager, editorHandler } = useSwivuBryntumContext();
  const history = useHistory();
  const [confirmDemote, setConfirmDemote] = useState(false);
  const {
    currentStep,
    goNext,
    goBack,
    canProceed,
    loading,
    submitting,
    promoted,
    onSubmit,
    isEditing,
    demoting,
    onDemote,
  } = usePromotePanel();

  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      return record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey;
    },
  });

  const hasOpenAIKey = useMemo(() => {
    if (!userPreferences) return false;
    return userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";
  }, [userPreferences]);

  const isLastStep = currentStep === PROMOTE_STEPS.length - 1;

  const handleDone = useCallback(() => {
    handleClosePromotePanel();
  }, [handleClosePromotePanel]);

  const handleViewAssets = useCallback(() => {
    setOpen(false);
    editorHandler.openEditor(
      EDITOR_KEYS.marketingAssetEditor,
      // @ts-expect-error - null recordIdObj when using by: "recordType"
      null,
      { entityRecord },
      { by: "recordType", useExisting: true },
    );
  }, [setOpen, entityManager, editorHandler, entityRecord]);

  const handleNavigateToSettings = useCallback(() => {
    if (!selectedBoard?.record) return;
    const id = StringHelper.generateUUID();
    // @ts-expect-error - selectedBoard.record type not fully defined
    const settingsUrl = `/${selectedBoard.record.slug}/settings`;
    history.push(settingsUrl, { viewId: id, viewType: VIEW_TYPES.settings });
    viewManager?.selectViewAnonymousView(id, "Settings", VIEW_TYPES.settings);
    handleClosePromotePanel();
    if (entityManager?.isOpen) {
      entityManager.closeEntityManager();
    }
  }, [selectedBoard, history, viewManager, handleClosePromotePanel, entityManager]);

  if (loading || isPreparingForPromote) {
    return (
      <div className="flex flex-col h-full min-h-0 bg-card">
        <PanelHeader onClose={handleClosePromotePanel} />
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-4 text-muted-foreground"
          >
            <div className="relative">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              >
                <Loader2 className="w-8 h-8" />
              </motion.div>
              <motion.div
                className="absolute inset-0 rounded-full"
                animate={{ opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  background: "radial-gradient(circle, hsl(var(--primary) / 0.2) 0%, transparent 70%)",
                }}
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">
                {isPreparingForPromote ? "Preparing image for promotion..." : "Analyzing image..."}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (promoted) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-card">
        <PromoteSuccessView onDone={handleDone} isEditing={isEditing} onViewAssets={handleViewAssets} />
      </div>
    );
  }

  if (!hasOpenAIKey) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-card">
        <PanelHeader onClose={handleClosePromotePanel} isEditing={isEditing} />
        <LockedState onNavigateToSettings={handleNavigateToSettings} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="shrink-0 border-b border-border px-4 pb-1 pt-2 flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <StepIndicator />
        </div>
        <Button type="button" variant="ghost" size="icon-sm" onClick={handleClosePromotePanel} className="shrink-0 mt-2">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {(() => {
          const StepComponent = STEP_COMPONENTS[currentStep];
          return <StepComponent />;
        })()}
      </div>

      <div className="shrink-0 border-t border-border px-4 py-3 space-y-2">
        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              className="shrink-0"
            >
              <Button type="button" variant="outline" onClick={goBack} className="px-5">
                Back
              </Button>
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            {isLastStep ? (
              <PromoteButton key="promote" submitting={submitting} onSubmit={onSubmit} isEditing={isEditing} />
            ) : (
              <motion.div
                key="continue"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1"
              >
                <Button
                  type="button"
                  onClick={goNext}
                  disabled={!canProceed}
                  className="w-full gap-2"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {isEditing && (
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmDemote(true)}
              disabled={demoting || submitting}
              className="w-full gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              {demoting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Ban className="w-3.5 h-3.5" />
              )}
              {demoting ? "Demoting..." : "Demote Asset"}
            </Button>

            <AlertDialog open={confirmDemote} onOpenChange={setConfirmDemote}>
              <AlertDialogContent className="z-[--swivu-bryntum-z-index]">
                <AlertDialogHeader>
                  <AlertDialogTitle>Demote Asset</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to demote this asset? This will remove its promotion status and all associated tags.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={onDemote}
                  >
                    Demote
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
};

const PromoteButton = ({
  submitting,
  onSubmit,
  isEditing,
}: {
  submitting: boolean;
  onSubmit: () => void;
  isEditing: boolean;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1"
    >
      <Button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        className="w-full gap-2"
      >
        <AnimatePresence mode="wait">
          {submitting ? (
            <motion.span
              key="submitting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              {isEditing ? "Saving..." : "Promoting..."}
            </motion.span>
          ) : (
            <motion.span
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2"
            >
              {isEditing ? <Save className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              {isEditing ? "Save Changes" : "Promote"}
            </motion.span>
          )}
        </AnimatePresence>
      </Button>
    </motion.div>
  );
};

const PanelHeader = ({
  onClose,
  stepLabel,
  isEditing,
}: {
  onClose: () => void;
  stepLabel?: string;
  isEditing?: boolean;
}) => (
  <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
    <div className="flex items-center gap-2">
      {isEditing ? (
        <Pencil className="w-4 h-4 text-primary" />
      ) : (
        <Sparkles className="w-4 h-4 text-primary" />
      )}
      <span className="text-sm font-medium text-foreground">
        {isEditing ? "Edit Promotion" : "Promote"}
      </span>
    </div>
    <div className="flex items-center gap-2">
      {stepLabel && (
        <span className="text-sm text-muted-foreground">{stepLabel}</span>
      )}
      <Button type="button" variant="ghost" size="icon-sm" onClick={onClose}>
        <X className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

export const PromotePanel = () => (
  <PromotePanelProvider>
    <PromotePanelInner />
  </PromotePanelProvider>
);
